"use client";

import { Fragment, useEffect, useState } from "react";

// Keeps exactly one of the three deck variants mounted, without ever changing
// what the server sent.
//
// The variants are picked with CSS (see PortfolioPreview), which is what keeps
// the markup identical on both sides of hydration — deciding in JavaScript with
// useReducedMotion() is what used to tear this section apart. That stays true
// here: `variant` is null on the server AND on the first client render, and null
// renders all three, so the tree React hydrates matches the HTML byte for byte.
// Only afterwards, in an effect, does the losing pair unmount.
//
// The point is what those two cost while mounted. Each deck builds three
// DeckLayers, and each DeckLayer holds six MotionValues driving a motion.div —
// so a phone was running the desktop deck's spring loop and its autoplay timer
// against a `display: none` subtree, plus the touch deck's drag controls, for a
// section that shows one deck. Hidden is not free; unmounted is.
//
// Media queries rather than the CSS classes themselves: the classes are the
// source of truth for what is visible, and these mirror them. `lg` is Tailwind's
// default 64rem/1024px, and the reduced-motion query matches `motion-reduce:`.
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const DESKTOP = "(min-width: 1024px)";

export default function DeckSwitcher({ arrows, swipe, stacked }) {
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    const reduced = window.matchMedia(REDUCED_MOTION);
    const desktop = window.matchMedia(DESKTOP);

    // Reduced motion wins over width, mirroring the `motion-reduce:!hidden` /
    // `motion-reduce:!block` overrides on the variants themselves.
    const sync = () =>
      setVariant(
        reduced.matches ? "stacked" : desktop.matches ? "arrows" : "swipe",
      );

    sync();
    reduced.addEventListener("change", sync);
    desktop.addEventListener("change", sync);
    return () => {
      reduced.removeEventListener("change", sync);
      desktop.removeEventListener("change", sync);
    };
  }, []);

  // The keyed Fragments are not decoration. These three elements are built in
  // PortfolioPreview, a Server Component, and reach us through the RSC payload —
  // so by the time React sees them here they carry neither a key nor an owner,
  // and rendering them as siblings makes React read them as an unkeyed list
  // ("Each child in a list should have a unique key prop. Check the top-level
  // render call using <DeckSwitcher>"). Keying by variant also ties each deck's
  // identity to which deck it is rather than to its position, so the winner
  // survives the switch instead of being remounted when the other two drop.
  const decks = [
    ["arrows", arrows],
    ["swipe", swipe],
    ["stacked", stacked],
  ];

  return decks
    .filter(([id]) => variant === null || variant === id)
    .map(([id, deck]) => <Fragment key={id}>{deck}</Fragment>);
}
